import { useState, useMemo, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Secondary line rendered under the label; also matched when searching. */
  sublabel?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Trigger text when `value` matches no option. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Applied to the trigger — use it to set the width, e.g. `w-[260px]`. */
  className?: string;
  /** Applied to the popover panel. Defaults to the trigger-matching width. */
  contentClassName?: string;
  'aria-label'?: string;
}

/**
 * Generic single-select combobox: Radix Popover + a plain search input + a
 * filtered button list. This is the repo's combobox idiom (see
 * `TimezoneCombobox` and `spaces/UserPicker`) — there is no `cmdk` /
 * shadcn `Command` here — extracted so long option lists get a search box
 * without a third hand-rolled copy.
 *
 * Use this over `RadixSelect` whenever the list can realistically exceed
 * ~15 entries.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches found.',
  disabled,
  className,
  contentClassName,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      `${o.label} ${o.sublabel ?? ''}`.toLowerCase().includes(q),
    );
  }, [options, search]);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label,
    [options, value],
  );

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn('justify-between font-normal text-left', className)}
        >
          <span className="truncate text-sm">{selectedLabel ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[var(--radix-popover-trigger-width)] p-0', contentClassName)}
        align="start"
      >
        {/* Fill matches the KB file search (AuiFiles' SearchBox): a muted
            background with a leading magnifier. A bare <input> carries no
            background token and renders white inside the popover in dark
            themes. Focus uses the app-wide `.focus-ring` utility, not that
            component's blue primary ring. */}
        <div className="relative flex items-center border-b p-2">
          <Search className="pointer-events-none absolute left-4 h-3.5 w-3.5 text-foreground/70" />
          <input
            ref={inputRef}
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-muted-foreground/30 bg-muted/60 py-1.5 pl-8 pr-2 text-sm text-foreground placeholder:text-foreground/60 focus-ring"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">{emptyMessage}</p>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={cn(
                  'flex w-full cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent',
                  opt.value === value && 'bg-accent font-medium',
                )}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    opt.value === value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{opt.label}</span>
                  {opt.sublabel && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {opt.sublabel}
                    </span>
                  )}
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
